(() => {
  "use strict";

  class DigiClubPlans {
    constructor(section) {
      this.section = section;

      this.slider =
        section.querySelector(
          "[data-digi-club-plans-slider]"
        );

      this.track =
        section.querySelector(
          "[data-digi-club-plans-track]"
        );

      this.cards = [
        ...section.querySelectorAll(
          "[data-digi-club-plans-card]"
        )
      ];

      /*
       * Abas do novo layout mobile.
       * Esse array ficará vazio nos modos carousel e stack,
       * porque o Liquid só renderiza as abas no modo tabs.
       */
      this.tabs = [
        ...section.querySelectorAll(
          "[data-digi-club-plans-tab]"
        )
      ];

      this.previousButton =
        section.querySelector(
          "[data-digi-club-plans-previous]"
        );

      this.nextButton =
        section.querySelector(
          "[data-digi-club-plans-next]"
        );

      this.dots = [
        ...section.querySelectorAll(
          "[data-digi-club-plans-dot]"
        )
      ];

      this.revealItems = [
        ...section.querySelectorAll(
          "[data-digi-club-plans-reveal]"
        )
      ];

      this.animationEnabled =
        section.dataset.animationEnabled === "true";

      this.mobileLayout =
        section.dataset.mobileLayout || "carousel";

      this.reduceMotionQuery =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        );

      this.currentIndex = 0;

      this.scrollTimer = null;
      this.resizeTimer = null;
      this.observer = null;

      /*
       * Bind dos métodos utilizados como listeners.
       * Isso permite remover corretamente os eventos no destroy().
       */
      this.handleScroll =
        this.handleScroll.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.handlePrevious =
        this.handlePrevious.bind(this);

      this.handleNext =
        this.handleNext.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleTabClick =
        this.handleTabClick.bind(this);

      this.handleReducedMotion =
        this.handleReducedMotion.bind(this);

      this.init();
    }

    /* ======================================================
       INITIALIZATION
    ====================================================== */

    init() {
      if (!this.track || !this.cards.length) {
        this.showAll();
        return;
      }

      this.bindEvents();
      this.initializeReveal();

      /*
       * Aplica o estado correto conforme o layout selecionado.
       */
      this.syncLayoutState();

      this.updateNavigation();
    }

    /* ======================================================
       EVENTS
    ====================================================== */

    bindEvents() {
      /*
       * O scroll do track só terá efeito no modo carrossel.
       * O evento pode continuar registrado porque
       * handleScroll() já verifica o modo atual.
       */
      this.track.addEventListener(
        "scroll",
        this.handleScroll,
        { passive: true }
      );

      this.track.addEventListener(
        "keydown",
        this.handleKeydown
      );

      window.addEventListener(
        "resize",
        this.handleResize,
        { passive: true }
      );

      this.previousButton?.addEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.addEventListener(
        "click",
        this.handleNext
      );

      /*
       * Eventos dos dots do carrossel.
       */
      this.dots.forEach(dot => {
        dot.addEventListener(
          "click",
          () => {
            const index =
              Number.parseInt(
                dot.dataset.planIndex,
                10
              );

            if (!Number.isFinite(index)) {
              return;
            }

            this.scrollToIndex(index);
          }
        );
      });

      /*
       * Eventos das novas abas.
       */
      this.tabs.forEach(tab => {
        tab.addEventListener(
          "click",
          this.handleTabClick
        );
      });

      if (
        typeof this.reduceMotionQuery.addEventListener ===
        "function"
      ) {
        this.reduceMotionQuery.addEventListener(
          "change",
          this.handleReducedMotion
        );
      } else if (
        typeof this.reduceMotionQuery.addListener ===
        "function"
      ) {
        this.reduceMotionQuery.addListener(
          this.handleReducedMotion
        );
      }
    }

    /* ======================================================
       LAYOUT MODES
    ====================================================== */

    isMobileViewport() {
      return window.innerWidth < 990;
    }

    isCarouselMode() {
      return (
        this.isMobileViewport() &&
        this.mobileLayout === "carousel"
      );
    }

    isTabsMode() {
      return (
        this.isMobileViewport() &&
        this.mobileLayout === "tabs"
      );
    }

    isStackMode() {
      return (
        this.isMobileViewport() &&
        this.mobileLayout === "stack"
      );
    }

    /*
     * Sincroniza o componente quando:
     * - a seção inicia;
     * - a janela muda de tamanho;
     * - o usuário sai do mobile para o desktop.
     */
    syncLayoutState() {
      if (this.isTabsMode()) {
        this.activateTab(
          this.currentIndex,
          {
            focus: false
          }
        );

        return;
      }

      /*
       * Fora do modo tabs, todos os cards precisam voltar
       * a ficar acessíveis.
       */
      this.resetTabsState();

      if (this.isCarouselMode()) {
        this.updateActiveIndex();
        return;
      }

      /*
       * Stack e desktop mostram todos os cards.
       */
      this.currentIndex = 0;

      this.updateDots();
      this.updateNavigation();
    }

    /* ======================================================
       TABS
    ====================================================== */

    handleTabClick(event) {
      if (!this.isTabsMode()) return;

      const clickedTab =
        event.currentTarget;

      const index =
        Number.parseInt(
          clickedTab.dataset.planIndex,
          10
        );

      if (!Number.isFinite(index)) {
        return;
      }

      this.activateTab(
        index,
        {
          focus: false
        }
      );
    }

    activateTab(
      index,
      options = {}
    ) {
      if (
        !this.tabs.length ||
        !this.cards.length
      ) {
        return;
      }

      const {
        focus = false
      } = options;

      const targetIndex =
        this.clamp(
          index,
          0,
          Math.min(
            this.tabs.length,
            this.cards.length
          ) - 1
        );

      this.currentIndex =
        targetIndex;

      this.tabs.forEach(
        (tab, tabIndex) => {
          const isActive =
            tabIndex === targetIndex;

          tab.classList.toggle(
            "is-active",
            isActive
          );

          tab.setAttribute(
            "aria-selected",
            isActive
              ? "true"
              : "false"
          );

          /*
           * Apenas a aba selecionada entra naturalmente
           * na navegação por teclado.
           */
          tab.setAttribute(
            "tabindex",
            isActive
              ? "0"
              : "-1"
          );

          if (
            isActive &&
            focus
          ) {
            tab.focus();
          }
        }
      );

      this.cards.forEach(
        (card, cardIndex) => {
          const isActive =
            cardIndex === targetIndex;

          card.classList.toggle(
            "is-active",
            isActive
          );

          card.setAttribute(
            "aria-hidden",
            isActive
              ? "false"
              : "true"
          );

          /*
           * Impede interação com botões e links
           * dos cards ocultos.
           */
          if (isActive) {
            card.removeAttribute(
              "inert"
            );

            /*
             * Garante que o card ativo apareça mesmo
             * se a animação de entrada ainda não tiver
             * sido disparada pelo observer.
             */
            card.classList.add(
              "is-visible"
            );
          } else {
            card.setAttribute(
              "inert",
              ""
            );
          }
        }
      );

      this.updateDots();
      this.updateNavigation();
    }

    resetTabsState() {
      this.tabs.forEach(
        (tab, index) => {
          const isFirst =
            index === 0;

          tab.classList.toggle(
            "is-active",
            isFirst
          );

          tab.setAttribute(
            "aria-selected",
            isFirst
              ? "true"
              : "false"
          );

          tab.setAttribute(
            "tabindex",
            isFirst
              ? "0"
              : "-1"
          );
        }
      );

      this.cards.forEach(card => {
        /*
         * Fora do modo tabs, a classe is-active não deve
         * controlar a visibilidade.
         */
        card.classList.remove(
          "is-active"
        );

        card.removeAttribute(
          "aria-hidden"
        );

        card.removeAttribute(
          "inert"
        );
      });
    }

    /* ======================================================
       REVEAL ANIMATION
    ====================================================== */

    initializeReveal() {
      if (
        !this.animationEnabled ||
        this.reduceMotionQuery.matches
      ) {
        this.showAll();
        return;
      }

      const items = [
        ...this.revealItems,
        ...this.cards
      ];

      if (!items.length) return;

      this.observer =
        new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) {
                return;
              }

              entry.target.classList.add(
                "is-visible"
              );

              this.observer?.unobserve(
                entry.target
              );
            });
          },
          {
            threshold: 0.12,
            rootMargin: "0px 0px -8% 0px"
          }
        );

      items.forEach(item => {
        this.observer.observe(item);
      });
    }

    showAll() {
      [
        ...this.revealItems,
        ...this.cards
      ].forEach(item => {
        item.classList.add(
          "is-visible"
        );
      });
    }

    /* ======================================================
       CAROUSEL SCROLL
    ====================================================== */

    handleScroll() {
      if (!this.isCarouselMode()) return;

      window.clearTimeout(
        this.scrollTimer
      );

      this.scrollTimer =
        window.setTimeout(() => {
          this.updateActiveIndex();
          this.updateNavigation();
        }, 70);
    }

    handleResize() {
      window.clearTimeout(
        this.resizeTimer
      );

      this.resizeTimer =
        window.setTimeout(() => {
          this.syncLayoutState();
          this.updateNavigation();
        }, 100);
    }

    handlePrevious() {
      if (!this.isCarouselMode()) return;

      this.scrollToIndex(
        this.currentIndex - 1
      );
    }

    handleNext() {
      if (!this.isCarouselMode()) return;

      this.scrollToIndex(
        this.currentIndex + 1
      );
    }

    /* ======================================================
       KEYBOARD
    ====================================================== */

    handleKeydown(event) {
      /*
       * Navegação por teclado no carrossel.
       */
      if (this.isCarouselMode()) {
        if (event.key === "ArrowLeft") {
          event.preventDefault();

          this.handlePrevious();
        }

        if (event.key === "ArrowRight") {
          event.preventDefault();

          this.handleNext();
        }

        if (event.key === "Home") {
          event.preventDefault();

          this.scrollToIndex(0);
        }

        if (event.key === "End") {
          event.preventDefault();

          this.scrollToIndex(
            this.cards.length - 1
          );
        }

        return;
      }

      /*
       * Navegação por teclado no modo tabs.
       */
      if (!this.isTabsMode()) return;

      const activeElement =
        document.activeElement;

      const currentTabIndex =
        this.tabs.indexOf(
          activeElement
        );

      /*
       * Só reage quando o foco estiver realmente
       * em uma das abas.
       */
      if (currentTabIndex < 0) {
        return;
      }

      let targetIndex =
        currentTabIndex;

      if (event.key === "ArrowLeft") {
        event.preventDefault();

        targetIndex =
          currentTabIndex <= 0
            ? this.tabs.length - 1
            : currentTabIndex - 1;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();

        targetIndex =
          currentTabIndex >=
          this.tabs.length - 1
            ? 0
            : currentTabIndex + 1;
      }

      if (event.key === "Home") {
        event.preventDefault();

        targetIndex = 0;
      }

      if (event.key === "End") {
        event.preventDefault();

        targetIndex =
          this.tabs.length - 1;
      }

      if (
        targetIndex ===
        currentTabIndex
      ) {
        return;
      }

      this.activateTab(
        targetIndex,
        {
          focus: true
        }
      );
    }

    handleReducedMotion(event) {
      if (!event.matches) return;

      this.destroyObserver();
      this.showAll();
    }

    /* ======================================================
       CAROUSEL POSITION
    ====================================================== */

    scrollToIndex(index) {
      if (
        !this.cards.length ||
        !this.isCarouselMode()
      ) {
        return;
      }

      const targetIndex =
        this.clamp(
          index,
          0,
          this.cards.length - 1
        );

      const targetCard =
        this.cards[targetIndex];

      if (!targetCard) return;

      const behavior =
        this.reduceMotionQuery.matches
          ? "auto"
          : "smooth";

      this.track.scrollTo({
        left: targetCard.offsetLeft,
        behavior
      });

      this.currentIndex =
        targetIndex;

      this.updateDots();
      this.updateNavigation();
    }

    updateActiveIndex() {
      if (
        !this.isCarouselMode() ||
        !this.cards.length
      ) {
        return;
      }

      const trackLeft =
        this.track.scrollLeft;

      let closestIndex = 0;
      let closestDistance =
        Infinity;

      this.cards.forEach(
        (card, index) => {
          const distance =
            Math.abs(
              card.offsetLeft -
              trackLeft
            );

          if (
            distance <
            closestDistance
          ) {
            closestDistance =
              distance;

            closestIndex =
              index;
          }
        }
      );

      this.currentIndex =
        closestIndex;

      this.updateDots();
    }

    /* ======================================================
       DOTS AND NAVIGATION
    ====================================================== */

    updateDots() {
      this.dots.forEach(
        (dot, index) => {
          const isActive =
            index ===
            this.currentIndex;

          dot.classList.toggle(
            "is-active",
            isActive
          );

          dot.setAttribute(
            "aria-current",
            isActive
              ? "true"
              : "false"
          );
        }
      );
    }

    updateNavigation() {
      if (
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const carouselActive =
        this.isCarouselMode();

      this.previousButton.disabled =
        !carouselActive ||
        this.currentIndex <= 0;

      this.nextButton.disabled =
        !carouselActive ||
        this.currentIndex >=
          this.cards.length - 1;
    }

    /* ======================================================
       UTILITIES
    ====================================================== */

    clamp(
      value,
      minimum,
      maximum
    ) {
      return Math.min(
        Math.max(
          value,
          minimum
        ),
        maximum
      );
    }

    destroyObserver() {
      if (!this.observer) return;

      this.observer.disconnect();
      this.observer = null;
    }

    /* ======================================================
       DESTROY
    ====================================================== */

    destroy() {
      this.destroyObserver();

      window.clearTimeout(
        this.scrollTimer
      );

      window.clearTimeout(
        this.resizeTimer
      );

      this.track?.removeEventListener(
        "scroll",
        this.handleScroll
      );

      this.track?.removeEventListener(
        "keydown",
        this.handleKeydown
      );

      window.removeEventListener(
        "resize",
        this.handleResize
      );

      this.previousButton?.removeEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.removeEventListener(
        "click",
        this.handleNext
      );

      this.tabs.forEach(tab => {
        tab.removeEventListener(
          "click",
          this.handleTabClick
        );
      });

      if (
        typeof this.reduceMotionQuery.removeEventListener ===
        "function"
      ) {
        this.reduceMotionQuery.removeEventListener(
          "change",
          this.handleReducedMotion
        );
      } else if (
        typeof this.reduceMotionQuery.removeListener ===
        "function"
      ) {
        this.reduceMotionQuery.removeListener(
          this.handleReducedMotion
        );
      }
    }
  }

  /* ========================================================
     SECTION INITIALIZATION
  ======================================================== */

  const initializeSections = root => {
    root
      .querySelectorAll(
        "[data-digi-club-plans]"
      )
      .forEach(section => {
        if (
          section.dataset
            .digiClubPlansInitialized ===
          "true"
        ) {
          return;
        }

        section.dataset
          .digiClubPlansInitialized =
          "true";

        section.__digiClubPlans =
          new DigiClubPlans(section);
      });
  };

  const unloadSection = section => {
    const plansSection =
      section.matches?.(
        "[data-digi-club-plans]"
      )
        ? section
        : section.querySelector?.(
            "[data-digi-club-plans]"
          );

    if (!plansSection) return;

    plansSection
      .__digiClubPlans
      ?.destroy();

    delete plansSection
      .__digiClubPlans;

    delete plansSection
      .dataset
      .digiClubPlansInitialized;
  };

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initializeSections(
          document
        );
      }
    );
  } else {
    initializeSections(
      document
    );
  }

  /* ========================================================
     SHOPIFY THEME EDITOR
  ======================================================== */

  document.addEventListener(
    "shopify:section:load",
    event => {
      initializeSections(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    event => {
      unloadSection(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:block:select",
    event => {
      const card =
        event.target.closest?.(
          "[data-digi-club-plans-card]"
        );

      if (!card) return;

      card.classList.add(
        "is-visible"
      );

      const section =
        card.closest(
          "[data-digi-club-plans]"
        );

      const instance =
        section?.__digiClubPlans;

      if (!instance) return;

      const index =
        instance.cards.indexOf(
          card
        );

      if (index < 0) return;

      /*
       * Quando o lojista seleciona um bloco no editor:
       *
       * Carrossel → rola até o card.
       * Abas → abre a aba correspondente.
       * Stack/Desktop → o card já está visível.
       */
      if (
        instance.isCarouselMode()
      ) {
        instance.scrollToIndex(
          index
        );

        return;
      }

      if (
        instance.isTabsMode()
      ) {
        instance.activateTab(
          index,
          {
            focus: false
          }
        );
      }
    }
  );
})();